import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

/* ── Terms of Use Modal ── */
const TermsModal = ({ onClose }) => (
  <div
    className="fixed inset-0 z-[999] flex items-center justify-center p-4"
    style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
    onClick={onClose}
  >
    <div
      className="relative bg-white text-[#1E1E1E] rounded-lg shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
      onClick={e => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-5 border-b border-[#D2D2D2] shrink-0">
        <h2 className="font-sans font-bold text-[22px] text-[#1E1E1E]">Terms of Use</h2>
        <button
          onClick={onClose}
          aria-label="Close"
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#EEEFF1] transition-colors text-[#4A4A4A] hover:text-[#1E1E1E]"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      {/* Scrollable body */}
      <div className="overflow-y-auto px-8 py-6 space-y-5 text-[14px] leading-relaxed text-[#4A4A4A]">
        <p>Please read the following information carefully before using this website. By using this website, you agree to these terms and conditions. If you do not agree to these terms and conditions, please do not use this website.</p>
        <p>These terms herein apply to all the users, customers, contractors, and suppliers of Takumi. The terms and conditions herein are in addition to and do not override, the specific terms and conditions that apply to the products or services offered by Takumi and otherwise through this site.</p>

        <Section n="1" title="Purpose of the Website">
          This website, as well as the contents including all information, text, graphics, images, hyperlinks and other material, is for general information purposes about Takumi. You are requested to contact our representatives to check out the details of any specific products and our capabilities. We provide services to the domestic and international markets.
        </Section>

        <Section n="2" title="Copyright">
          © Takumi Motion Controls Pvt. Ltd. All rights reserved. No part of the Takumi website, logo, images, content, and other assets may be reproduced, modified, or distributed in any form or manner without the prior written permission of Takumi Motion Controls Pvt. Ltd.
        </Section>

        <Section n="3" title="Links to Other Websites">
          For purposes of convenience, the Takumi site contains links to third-party Web sites. Takumi makes no representations or warranties about any third-party Web site that you may access through the links. By providing a link to those Websites, Takumi does not endorse, adopt, or otherwise accept any responsibility for the content or use of those Websites.
        </Section>

        <Section n="4" title="Updating Website">
          <p>Takumi has the right to change, modify or add to the terms and conditions governing this site, or any part thereof, the information, products, or services offered through this site. Such changes, modifications, additions, or deletions shall be effective immediately upon notice thereof, which may be given by any means including posting on this site or by other electronic or conventional means. You agree to review this page periodically to be aware of any such changes, modifications, additions or deletions to the terms and conditions stated herein. Your continued use of the site after such notice has been given shall be deemed to constitute acceptance of the changes, modifications, additions or deletions.</p>
          <p className="mt-3">We make all reasonable efforts to ensure that this website is accessible at all times. You may however be prevented from accessing the site or experience access problems due to technical difficulties or during maintenance work or upgrades. In addition, while we take reasonable care to ensure that the information on this site is kept up to date, it makes no representation or warranty, express or implied, of its accuracy and completeness.</p>
          <p className="mt-3">We do not guarantee and make no representations or warranties that this website and access thereto are secure, or that this website is free of viruses or bugs that could result in damage to or the loss of data. You are responsible for protecting your data from viruses.</p>
        </Section>

        <Section n="5" title="Limitation of Liability">
          <p>Information and products or services offered on this website are provided "as is" without any express or implied warranty of any kind, including warranties of merchantability, fitness for a particular purpose, or non-infringement.</p>
          <p className="mt-3">In no event will Takumi be liable to any party for any damages of any kind, including but not limited to direct, indirect, special or consequential damages arising out of the use or inability to use this website, or any linked website, including without limitation, lost profits, loss of use, business interruption, or other economic losses, loss of programs or other data, whether in an action of contract, negligence or other tortious action, even if Takumi is advised of the possibility of such damages.</p>
          <p className="mt-3">In no event will Takumi warrant or guarantee the correctness, comprehensiveness, completeness, accuracy, timeliness, merchantability or fitness for any particular use or purpose of any information, products, or services on this website.</p>
        </Section>

        <Section n="6" title="Confidential Information">
          We are not receiving confidential or proprietary information from you through our Web Site. Please note that any information or material sent to us will not be deemed to be confidential. By sending any information or material, you grant us an unrestricted, irrevocable license to use, reproduce, display, perform, modify, transmit and distribute those materials or information, and you also agree that we are free to use any ideas, concepts, know-how or techniques that you send us for any purpose. The pricing details shall be considered confidential.
        </Section>

        <Section n="7" title="Jurisdiction">
          These Terms and Conditions shall be governed by and construed in accordance with the laws of India and under the exclusive jurisdiction of the Bangalore courts. If any provision of this agreement shall be unlawful, void, or for any reason unenforceable, then that provision shall be deemed severable from these Terms and Conditions and shall not affect the validity and enforceability of any remaining provisions. This is the entire agreement between the parties relating to the subject matter herein and shall not be modified except in writing, signed by both parties.
        </Section>
      </div>

      {/* Footer */}
      <div className="px-8 py-4 border-t border-[#D2D2D2] shrink-0 flex justify-end">
        <button
          onClick={onClose}
          className="px-6 py-2 bg-[#D64545] text-white text-[14px] font-medium rounded hover:bg-[#c23b3b] transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

const Section = ({ n, title, children }) => (
  <div>
    <h3 className="font-sans font-semibold text-[15px] text-[#1E1E1E] mb-2">{n}. {title}</h3>
    <div className="text-[14px] leading-relaxed text-[#4A4A4A]">{children}</div>
  </div>
);

/* ── Privacy Policy Modal ── */
const PrivacyModal = ({ onClose }) => (
  <div
    className="fixed inset-0 z-[999] flex items-center justify-center p-4"
    style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
    onClick={onClose}
  >
    <div
      className="relative bg-white text-[#1E1E1E] rounded-lg shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
      onClick={e => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-5 border-b border-[#D2D2D2] shrink-0">
        <h2 className="font-sans font-bold text-[22px] text-[#1E1E1E]">Privacy Policy</h2>
        <button
          onClick={onClose}
          aria-label="Close"
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#EEEFF1] transition-colors text-[#4A4A4A] hover:text-[#1E1E1E]"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      {/* Scrollable body */}
      <div className="overflow-y-auto px-8 py-6 space-y-5 text-[14px] leading-relaxed text-[#4A4A4A]">
        <p>Takumi Motion Controls Pvt. Ltd. (hereinafter referred to as "Company", "Takumi", "we", "us", "our") is committed to protecting the security of your personal information and safeguarding your privacy. This privacy policy sets out the details of the personal data/information collected, the manner in which it is collected, by whom, as well as the purposes for which it is used.</p>
        <p>This Policy is intended to set minimum standard controls with respect to the handling of Personal Information in accordance with the applicable laws, rules and regulations.</p>
        <p>This Privacy Policy may be revised and shall be updated on the website from time to time.</p>

        <Section n="1" title="What is personal data / information?">
          For the purpose of this Policy "personal data/information" means any data or information which relates to a natural person if that person can, whether directly or indirectly in conjunction with any other data, be identified from it and includes sensitive personal information as defined by applicable laws, rules &amp; regulations.
        </Section>

        <Section n="2" title="Collection of Information">
          <ul className="space-y-2 list-none">
            <BulletItem>Takumi will generally collect your personal information directly from you when you register with Takumi Website / social media accounts.</BulletItem>
            <BulletItem>Takumi may also collect your personal information through other methods while interactions with us through physical visit, post, telephone, email, writing to us or from a third-party source, such as our dealers, vendors, business partners, government authorities or any other source which are legally acquired by us.</BulletItem>
            <BulletItem>Takumi will not collect personal information unless the information is necessary for one or more of its functions or activities and the information will only be collected by lawful and fair means not in any un-reasonably intrusive way.</BulletItem>
            <BulletItem>We use cookies to personalize content and ads, to provide social media features and to analyze data about webpage traffic and improve our website in order to tailor it to customer needs. By clicking ACCEPT or continuing to browse this site you are agreeing to our use of cookies.</BulletItem>
          </ul>
        </Section>

        <Section n="3" title="Use & processing of Information">
          <ul className="space-y-2 list-none">
            <BulletItem>By visiting Takumi website / social media accounts, and providing your information, you agreed to the processing, transfer, and disclosure of your personally identifiable information in accordance with this Policy.</BulletItem>
            <BulletItem>Information is collected and stored in a manner which is appropriate to the nature of the data. Such information is used for Business, Marketing and Promotional purposes or to enabling and monitoring your use of our Sites, which help us to improve our services.</BulletItem>
            <BulletItem>Takumi shall obtain consent from you w.r.t the purpose of its use, if required by applicable laws, regulations, or industry standards. Takumi need not obtain consent or give notice, if the collection or use of Personal Information is necessary for the completion of a contract or business arrangement with you, and you have agreed in advance to such collection or use or due to legal requirements.</BulletItem>
          </ul>
        </Section>

        <Section n="4" title="Disclosure & Transfer of Personal information">
          <ul className="space-y-2 list-none">
            <BulletItem>Takumi may outsource the collection/use of your Personal Information to their Business partners also, where it is necessary. In that case, Takumi shall ensure that the Business Partners has sufficient systems, policies, and practices in place to protect your Personal Information.</BulletItem>
            <BulletItem>We DO NOT use, transfer or disclose Information for purposes other than as mentioned in this Policy, except with the consent of the user providing such Information or as required by applicable laws, rules and regulations.</BulletItem>
          </ul>
        </Section>

        <Section n="5" title="Retention and Suspension">
          <ul className="space-y-2 list-none">
            <BulletItem><strong>Retention:</strong> The Company may retain the Personal Information provided by you for the period as long as necessary to meet legal or regulatory requirements or for legitimate business purposes.</BulletItem>
            <BulletItem>
              <strong>Suspension:</strong> The Company may delete or suspend use of the Personal Information when the following criteria are met:
              <ul className="mt-2 space-y-1 pl-4">
                <li className="flex gap-2"><span className="text-[#D64545] shrink-0">•</span><span>If the Personal Information is no longer necessary for the purpose it was collected or used;</span></li>
                <li className="flex gap-2"><span className="text-[#D64545] shrink-0">•</span><span>If the valid period of consent to keep the data has expired and there is not any legal ground to store/process the data; and</span></li>
                <li className="flex gap-2"><span className="text-[#D64545] shrink-0">•</span><span>To comply with legal retention obligations or in accordance with applicable statutory limitation periods.</span></li>
              </ul>
            </BulletItem>
          </ul>
        </Section>

        <Section n="6" title="Rights">
          If you wish to amend or delete any personally identifiable information that you have previously provided to us or want to withdraw your consent to the retention/storage/processing of your Personal Information, please send us a mail or call us. We will take the necessary steps as per your request.
        </Section>

        <Section n="7" title="Data Security">
          The security of your information is very important to Takumi &amp; we will make our best effort to ensure security on our systems. Our Servers/database stores Personal Information in a secure manner with authorized access &amp; safeguard it from loss/leakage, misuse, wrongful disclosure, destruction, and alteration. We cannot guarantee the security of your personal information transmitted to us via Internet, although we use our best endeavors to do so. Takumi will not be liable in any way in relation to breach of security during internet transmission of personal information.
        </Section>

        <Section n="8" title="Grievance">
          In case of any complaint/grievance or to report any violations (data loss/leakage), you may contact us.
        </Section>

        <Section n="9" title="Jurisdiction">
          The Privacy Policy shall be governed by the laws of India and the Courts in Bangalore shall have exclusive jurisdiction in respect of all matters arising out of, or concerning or incidental to this Privacy Policy.
        </Section>
      </div>

      {/* Footer */}
      <div className="px-8 py-4 border-t border-[#D2D2D2] shrink-0 flex justify-end">
        <button
          onClick={onClose}
          className="px-6 py-2 bg-[#D64545] text-white text-[14px] font-medium rounded hover:bg-[#c23b3b] transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

const BulletItem = ({ children }) => (
  <li className="flex gap-2">
    <span className="text-[#D64545] shrink-0 mt-0.5">a.</span>
    <span>{children}</span>
  </li>
);

/* ── Social icon square box ── */
const SocialBox = ({ href, label, border = true, className = '', children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    className={`flex items-center justify-center w-9 h-9 md:w-11 md:h-11 text-white hover:bg-white/10 transition-colors duration-200 ${border ? 'border border-white/40' : ''} ${className}`}
  >
    {children}
  </a>
);

const XIcon = () => (
  <svg className="w-4 h-4 md:w-5 md:h-5 fill-current" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <circle cx="12" cy="12" r="4"/>
    <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg className="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6z"/>
    <rect x="2" y="9" width="4" height="12"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
);

/* ── Shared SVG icons ── */
const PhoneIcon = () => (
  <svg className="w-3 h-3 shrink-0 fill-none stroke-current" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.387a12.035 12.035 0 01-7.108-7.108c-.155-.44.01-1.21.387-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"/>
  </svg>
);

const MailIcon = () => (
  <svg className="w-3 h-3 shrink-0 fill-none stroke-current" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"/>
  </svg>
);

const Footer = () => {
  const { t } = useLanguage();
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  return (
    <footer data-testid="footer" className="relative bg-[#D64545] text-white overflow-hidden w-full select-none">
      {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyModal onClose={() => setShowPrivacy(false)} />}

      {/* ── TAKUMI watermark ── */}
      <div aria-hidden="true" className="pointer-events-none absolute left-0 right-0 bottom-0 w-full z-0">
        <svg viewBox="0 0 1000 235" width="100%" className="w-full block pointer-events-none" overflow="visible" style={{ pointerEvents: 'none' }}>
          <text
            x="-18" y="235"
            textLength="1036"
            lengthAdjust="spacingAndGlyphs"
            style={{ fontSize: '240px', fontWeight: 900, fill: '#D43B3B', letterSpacing: 0, stroke: '#D43B3B', strokeWidth: '5px', paintOrder: 'stroke fill' }}
          >
            TAKUMI
          </text>
        </svg>
      </div>

      {/* ══════════════════════════════════════════════
          MOBILE LAYOUT  (hidden on sm and above)
      ══════════════════════════════════════════════ */}
      <div className="relative z-10 sm:hidden max-w-[1600px] mx-auto px-4 pt-8 pb-6">

        {/* 1. Company Logo */}
        <img
          src="/white logo.png"
          alt="Takumi Motion Controls"
          width="200" height="32"
          className="h-8 object-contain object-left mb-5"
        />

        {/* 2 + 3. Address 1 (India) & Quick Links — same horizontal row */}
        <div className="grid grid-cols-[1fr_auto] gap-x-8 items-start mb-5">

          {/* Address 1 — India */}
          <div className="space-y-1.5 text-[11px] font-normal text-white/90">
            <p className="leading-relaxed">
              2nd Cross, Wheelers Road Ext,<br />
              Da Costa Layout,<br />
              St. Thomas Town, Bengaluru – 560084,<br />
              Karnataka, India
            </p>
            <div className="flex items-center gap-1.5">
              <PhoneIcon />
              <span>+91 91 8720 6241</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MailIcon />
              <a href="mailto:info@takumi.co.in" className="hover:underline">info@takumi.co.in</a>
            </div>
          </div>

          {/* Quick Links — right of Address 1 */}
          <div>
            <h4 className="font-sans text-[12px] mb-2 tracking-wide" style={{ fontWeight: 500 }}>
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-1.5 text-[11px] font-normal text-white/85">
              <li><Link to="/"          className="hover:text-white transition-colors">{t('footer.links.home')}</Link></li>
              <li><Link to="/ecosystem" className="hover:text-white transition-colors">{t('footer.links.ecosystem')}</Link></li>
              <li><Link to="/about"     className="hover:text-white transition-colors">{t('footer.links.about')}</Link></li>
              <li><Link to="/contact"   className="hover:text-white transition-colors">{t('footer.links.contact')}</Link></li>
            </ul>
          </div>
        </div>

        {/* 4. Address 2 — Japan (left-aligned with Address 1) */}
        <div className="space-y-1.5 text-[11px] font-normal text-white/90 pt-5 border-t border-white/10 mb-5">
          <p className="leading-relaxed">
            {t('footer.japanAddress').map((line, i, arr) => (
              <React.Fragment key={i}>
                {line}{i < arr.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
          <div className="flex items-center gap-1.5">
            <MailIcon />
            <a href="mailto:info@takumi.co.in" className="hover:underline">info@takumi.co.in</a>
          </div>
        </div>

        {/* 5. Socials — heading + icons centered */}
        <div className="mb-6 text-center">
          <h4 className="font-sans text-[12px] mb-2 tracking-wide" style={{ fontWeight: 500 }}>
            {t('footer.socials')}
          </h4>
          <div className="flex flex-row items-center justify-center gap-5">
            <SocialBox href="https://twitter.com/takumitweets"                      label="X (Twitter)" border={false}><XIcon /></SocialBox>
            <SocialBox href="https://www.instagram.com/takumimotioncontrols"        label="Instagram"   border={false}><InstagramIcon /></SocialBox>
            <SocialBox href="https://www.linkedin.com/company/takumimotioncontrols" label="LinkedIn"    border={false}><LinkedInIcon /></SocialBox>
          </div>
        </div>

        {/* 6 + 7. Legal — divider, Terms | Privacy centered, then Copyright at very bottom */}
        <div className="border-t border-white/10 pt-4 flex flex-col items-center gap-3">
          {/* 6. Terms of Use | Privacy Policy — centered, pipe-separated */}
          <div className="flex items-center gap-2 text-[11px] font-normal text-white/80">
            <button onClick={() => setShowTerms(true)} className="hover:text-white transition-colors cursor-pointer bg-transparent border-none p-0 font-normal text-inherit">{t('footer.terms')}</button>
            <span className="text-white/40">|</span>
            <button onClick={() => setShowPrivacy(true)} className="hover:text-white transition-colors cursor-pointer bg-transparent border-none p-0 font-normal text-inherit">{t('footer.privacy')}</button>
          </div>
          {/* 7. Copyright — centered, smallest text, single line */}
          <p className="text-[11px] font-normal text-white/70 text-center whitespace-nowrap">
            {t('footer.copyright')}
          </p>
        </div>

      </div>

      {/* ══════════════════════════════════════════════
          DESKTOP LAYOUT  (existing, hidden on mobile)
      ══════════════════════════════════════════════ */}
      <div className="relative z-10 hidden sm:block max-w-[1600px] mx-auto px-6 lg:px-8 pt-8 pb-6 md:pt-14 md:pb-10">

        {/* ── Logo above, then 4 equal columns: Addr1 | Addr2 | Quick Links | Social ── */}

        {/* Logo */}
        <div className="mb-4 md:mb-8 w-1/2">
          <img
            src="/white logo.png"
            alt="Takumi Motion Controls"
            width="200" height="48"
            className="h-8 md:h-12 object-contain object-left"
          />
        </div>

        {/* 4 equal-gap columns */}
        <div className="grid grid-cols-4 gap-x-6 md:gap-x-10 items-start">

          {/* Col 1: India address */}
          <div className="space-y-1.5 md:space-y-3 text-[11px] md:text-[14px] font-normal text-white/90">
            <p className="leading-relaxed">
              2nd Cross, Wheelers Road Ext,<br />
              Da Costa Layout,<br />
              St. Thomas Town, Bengaluru – 560084,<br />
              Karnataka, India
            </p>
            <div className="flex items-center gap-1.5 md:gap-2.5">
              <svg className="w-3 h-3 md:w-4 md:h-4 shrink-0 fill-none stroke-current" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.387a12.035 12.035 0 01-7.108-7.108c-.155-.44.01-1.21.387-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"/>
              </svg>
              <span>+91 91 8720 6241</span>
            </div>
            <div className="flex items-center gap-1.5 md:gap-2.5">
              <svg className="w-3 h-3 md:w-4 md:h-4 shrink-0 fill-none stroke-current" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"/>
              </svg>
              <a href="mailto:info@takumi.co.in" className="hover:underline">info@takumi.co.in</a>
            </div>
          </div>

          {/* Col 2: Japan address */}
          <div className="space-y-1.5 md:space-y-3 text-[11px] md:text-[14px] font-normal text-white/90">
            <p className="leading-relaxed">
              {t('footer.japanAddress').map((line, i, arr) => (
                <React.Fragment key={i}>
                  {line}{i < arr.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
            <div className="flex items-center gap-1.5 md:gap-2.5">
              <svg className="w-3 h-3 md:w-4 md:h-4 shrink-0 fill-none stroke-current" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"/>
              </svg>
              <a href="mailto:info@takumi.co.in" className="hover:underline">info@takumi.co.in</a>
            </div>
          </div>

          {/* Col 3: Quick Links */}
          <div className="-mt-6 md:-mt-12">
            <h4 className="font-sans text-[12px] md:text-[18px] mb-2 md:mb-5 tracking-wide" style={{ fontWeight: 500 }}>
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-1.5 md:space-y-3 text-[11px] md:text-[14px] font-normal text-white/85">
              <li><Link to="/"          className="hover:text-white transition-colors">{t('footer.links.home')}</Link></li>
              <li><Link to="/ecosystem" className="hover:text-white transition-colors">{t('footer.links.ecosystem')}</Link></li>
              <li><Link to="/about"     className="hover:text-white transition-colors">{t('footer.links.about')}</Link></li>
              <li><Link to="/contact"   className="hover:text-white transition-colors">{t('footer.links.contact')}</Link></li>
            </ul>
          </div>

          {/* Col 4: Social */}
          <div className="-mt-6 md:-mt-12">
            <h4 className="font-sans text-[12px] md:text-[18px] mb-2 md:mb-5 tracking-wide" style={{ fontWeight: 500 }}>
              {t('footer.socials')}
            </h4>
            <div className="flex flex-col md:flex-row md:items-center gap-0.5 md:gap-4 -ml-0.5">
              <SocialBox href="https://twitter.com/takumitweets"                      label="X (Twitter)" border={false} className="-ml-1 md:-ml-1.5"><XIcon /></SocialBox>
              <SocialBox href="https://www.instagram.com/takumimotioncontrols"        label="Instagram"   border={false}><InstagramIcon /></SocialBox>
              <SocialBox href="https://www.linkedin.com/company/takumimotioncontrols" label="LinkedIn"    border={false}><LinkedInIcon /></SocialBox>
            </div>
          </div>

        </div>
      </div>

      {/* ── Desktop legal bar (hidden on mobile — legal is inside mobile block above) ── */}
      <div className="relative z-10 hidden sm:block border-t border-white/10 max-w-[1600px] mx-auto px-6 lg:px-8 py-4 md:py-5">
        <div className="flex flex-col items-center gap-2 text-[12px] font-normal">
          <div className="flex items-center gap-4 text-white/80">
            <button onClick={() => setShowTerms(true)} className="hover:text-white transition-colors cursor-pointer bg-transparent border-none p-0 font-normal text-inherit">{t('footer.terms')}</button>
            <span className="text-white/40">|</span>
            <button onClick={() => setShowPrivacy(true)} className="hover:text-white transition-colors cursor-pointer bg-transparent border-none p-0 font-normal text-inherit">{t('footer.privacy')}</button>
          </div>
          <p className="text-white/70">{t('footer.copyright')}</p>
        </div>
      </div>

    </footer>
  );
};

export default Footer;
